/**
 * Utility global-search-jcr-utility
 * @description Utility to access jcr configuration and hold relative helper  functions
 * @copyright 2021-7-20
 * @file
 */

/**
 * The GlobalSearchJcrUtility.
 * This utility is responsible for providing common methods to access jcr configuration.
 * @export
 * @class
 */
class GlobalSearchJcrUtility {
  /**
   * @param {object} options - instance options to be initialized with.
   */
  constructor(options = {
    site: '',
    searchJcrData: {}
  }) {
    this.site = options.site;
    this.jcr = options.searchJcrData;
    this.moduleInstance = options && options.moduleInstance;
  }

  /**
   * @param {object} options - Used to get the main component settings from JCR.
   * @returns {object} - Components Settings.
   */
  getComponentsSetting(options = {
    inputExperience: '',
    resultsExperience: '',
    componentName: ''
  }) {
    const key = `${this.site}_${options.inputExperience}_${options.resultsExperience}`;
    return this.jcr.componentsSettings[key.toLowerCase()][options.componentName];
  }

  /**
   * @param {object} options - used to get the Key for URL.
   * @returns {object} - specific service URL.
   */
  getUrl(options = {
    key: '',
    secureAlways: false
  }) {
    let url = this.jcr.componentsSettings.urls[options.key];
    if (url) {
      //if already an absolute url
      if (url.indexOf('http') === 0) {
        return url;
      } else {
        let secureHost;
        let wwwHost;
        if (this.moduleInstance.Widget && this.moduleInstance.Widget.siteParams) {
          secureHost = this.moduleInstance.Widget.siteParams.secureHost;
          wwwHost = this.moduleInstance.Widget.siteParams.wwwHost;
        }
        //if secureHost is required irrespective of Auth status
        if (options.secureAlways === true) {
          return secureHost + url;
        } else {
          if (this.moduleInstance.isAuth === true) {
            return secureHost + url;
          } else {
            return wwwHost + url;
          }
        }
      }
    }
    return;
  }

  /**
   * @returns {object} urlsList - all available service URLs.
   */
  getAllUrls() {
    return this.jcr.componentsSettings.urls;
  }

  /**
   * @param {object} options - used to get the Key for specific label.
   * @returns {object} label - specific string resource value based on key passed.
   */
  getLabel(options = {
    key: '',
    default: ''
  }) {
    return this.jcr.componentsSettings.stringResource[options.key];
  }

  /**
   * @returns {object} labelsColn - all the string resources for requested language.
   */
  getAllLabels() {
    return this.jcr.componentsSettings.stringResource;
  }

  /**
   * @param {object} options - used to get the Key.
   * @returns {object} phoneNo - phone numbers available for that key.
   */
  getPhoneNumber(options = {
    key: '',
    default: ''
  }) {
    return this.jcr.componentsSettings.phoneNumbers[options.key];
  }

  /**
   * @returns {object} settingsColn - all settings.
   */
  getAllSettings() {
    return this.jcr.componentsSettings.settings;
  }

  /**
   * @param {object} options - used to get the Key for URL.
   * @returns {object} objSetting - specific setting.
   */
  getSettings(options = {
    key: '',
    default: ''
  }) {
    return this.jcr.componentsSettings.settings[options.key];
  }

  /**
   * @returns {object} searchData - search data.
   */
  getSearchData() {
    return this.jcr.componentsSettings.searchData;
  }
}

export { GlobalSearchJcrUtility as default };
